import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiExternalLinkLine,
} from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';

import { ButtonIcon } from '@spotify-internal/encore-web';
import {
  MODULE_STYLES,
  ThinkingSpinner,
  extractLastNameComponent,
  isResourceName,
} from '@spotify/confidence-core-react';
import {
  AttachmentFragment,
  isNotFoundError,
} from '@spotify/confidence-graphql';
import {
  DefaultArtifactListItem,
  createArtifactExtension,
} from '@spotify/plugin-confidence-artifacts-react';
import {
  type PulseCaptureState,
  usePulse,
  usePulseCaptureState,
} from '@spotify/plugin-confidence-pulse-react';

import { confidencePulsePlugin } from '../plugin';
import { pulsesPageRouteRef } from '../routes';

const usePulseHref = (uri: string): string | undefined => {
  const pulsesPage = useRouteRef(pulsesPageRouteRef);
  const resourceName = uri.replace(/^\/\//, '');
  const pulseId = extractLastNameComponent(resourceName);
  if (!pulsesPage || !pulseId) return undefined;
  return `${pulsesPage()}/${pulseId}`;
};

const isPulseArtifact = (artifact: AttachmentFragment): boolean => {
  const resourceName = artifact.uri.replace(/^\/\//, '');
  return isResourceName(resourceName, 'pulses');
};

const PulseGoToButton = ({ artifact }: { artifact: AttachmentFragment }) => {
  const href = usePulseHref(artifact.uri);

  if (!href) {
    return null;
  }

  return (
    <ButtonIcon
      variant="tertiary"
      size="small"
      onClick={() => window.open(href, '_blank')}
      aria-label="Go to pulse"
    >
      <RiExternalLinkLine size={16} />
    </ButtonIcon>
  );
};

export const PulseArtifactListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onHide,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onHide?: () => void;
}) => {
  const href = usePulseHref(artifact.uri);
  const navigate = useNavigate();
  const pulseName = artifact.uri.replace(/^\/\//, '');
  const captureState = usePulseCaptureState(pulseName);
  const { error } = usePulse({
    name: pulseName,
    fetchPolicy: 'network-only',
  });
  const pulseNotFound = isNotFoundError(error);
  useEffect(() => {
    if (pulseNotFound) onHide?.();
  }, [onHide, pulseNotFound]);
  const handleSelect = href ? () => navigate(href) : onSelect;
  const status = {
    added: {
      icon: (
        <RiCheckboxCircleLine size={16} color="var(--essential-positive)" />
      ),
      secondary: 'Added to pulse · Open pulse',
    },
    adding: {
      icon: <ThinkingSpinner size="medium" />,
      secondary: 'Adding to pulse…',
    },
    failed: {
      icon: <RiErrorWarningLine size={16} color="var(--essential-negative)" />,
      secondary: 'Pulse update needs attention · Open pulse',
    },
    persisted: {
      icon,
      secondary: undefined,
    },
  } satisfies Record<
    PulseCaptureState,
    { icon: ReactNode; secondary: string | undefined }
  >;
  const presentation = status[captureState];

  if (pulseNotFound) {
    return null;
  }

  return (
    <DefaultArtifactListItem
      artifact={artifact}
      icon={presentation.icon}
      href={href}
      onSelect={handleSelect}
      secondary={presentation.secondary}
      disabled={captureState === 'adding'}
      onDelete={captureState === 'adding' ? undefined : onDelete}
    />
  );
};

const pulseIcon = {
  Icon: MODULE_STYLES.pulses.Icon,
  color: MODULE_STYLES.pulses.color,
};

export const PulseArtifactExtension = confidencePulsePlugin.provide(
  createArtifactExtension({
    id: 'pulse',
    predicate: isPulseArtifact,
    icon: () => pulseIcon,
    label: 'Pulse',
    category: 'Resources',
    renderHeaderActions: artifact => <PulseGoToButton artifact={artifact} />,
    render: () => null,
    renderListItem: (artifact, { onSelect, onDelete, onHide, icon }) => (
      <PulseArtifactListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onHide={onHide}
      />
    ),
  }),
);
